import { useCallback, useEffect, useRef, useState } from 'react';
import { MEDIA_PLAYING_TIMESTAMP_KEY } from '../constants/inactivityTimer';

export interface UseIdleTimerOptions {
  timeout: number;
  promptBeforeIdle?: number;
  stopOnIdle?: boolean;
  crossTab?: boolean;
  debounce?: number;
  disabled?: boolean;
  onAction?: (
    event: Event,
    idleTimer: { getRemainingTime: () => number },
  ) => void;
  onIdle?: () => void;
  onActive?: () => void;
  onPrompt?: () => void;
}

export interface UseIdleTimerReturn {
  activate: () => void;
  getRemainingTime: () => number;
  start: () => void;
  pause: () => void;
  resume: () => void;
  isEnabled: () => boolean;
}

const BROADCAST_CHANNEL_NAME = 'idle-timer-sync';
const STALE_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Check if any media (video or audio) is currently playing by checking localStorage timestamp.
 * Returns true if timestamp exists and is fresh (updated within the last 10 seconds).
 * If timestamp is stale or invalid, it's cleaned up and returns false.
 */
const isMediaPlaying = (): boolean => {
  const timestamp = parseInt(
    localStorage.getItem(MEDIA_PLAYING_TIMESTAMP_KEY) || '0',
    10,
  );

  // Validate timestamp: must be a valid number and greater than 0
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    // Clean up invalid data
    localStorage.removeItem(MEDIA_PLAYING_TIMESTAMP_KEY);
    return false;
  }

  const isStale = Date.now() - timestamp > STALE_TIMEOUT_MS;

  // If stale, clean it up
  if (isStale) {
    localStorage.removeItem(MEDIA_PLAYING_TIMESTAMP_KEY);
    return false;
  }

  return true;
};

export function useIdleTimer({
  timeout,
  promptBeforeIdle = 0,
  stopOnIdle = false,
  crossTab = false,
  debounce = 0,
  disabled = false,
  onAction,
  onIdle,
  onActive,
  onPrompt,
}: UseIdleTimerOptions): UseIdleTimerReturn {
  const [isPaused, setIsPaused] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const promptShownRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promptTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const onActionRef = useRef(onAction);
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);
  const onPromptRef = useRef(onPrompt);
  const timerStartTimeRef = useRef<number | null>(null);
  const elapsedTimeRef = useRef<number>(0);
  const resetTimersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onActionRef.current = onAction;
    onIdleRef.current = onIdle;
    onActiveRef.current = onActive;
    onPromptRef.current = onPrompt;
  }, [onAction, onIdle, onActive, onPrompt]);

  // clearTimers doesn't need a ref because:
  // 1. It's stable (empty dependency array)
  // 2. It only uses refs internally (no closure dependencies)
  // 3. It's already in dependency arrays where needed
  // 4. When called from resetTimers (via resetTimersRef), it uses the latest version from resetTimers' closure.
  // In contrast, functions like resetTimers are stored in refs (e.g. resetTimersRef) so that long-lived
  // listeners/callbacks, which are created once, can always call the latest implementation without needing
  // to be re-registered when dependencies change. This is the general "function-in-ref" pattern used in this hook.
  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (promptTimeoutIdRef.current) {
      clearTimeout(promptTimeoutIdRef.current);
      promptTimeoutIdRef.current = null;
    }
  }, []);

  const resetTimers = useCallback(() => {
    // Don't reset timers if media is playing
    if (disabled || isPaused || isMediaPlaying()) {
      return;
    }

    clearTimers();

    elapsedTimeRef.current = 0;
    timerStartTimeRef.current = Date.now();
    const remainingTime = timeout;

    if (promptBeforeIdle > 0 && remainingTime > promptBeforeIdle) {
      const promptTime = remainingTime - promptBeforeIdle;
      promptTimeoutIdRef.current = setTimeout(() => {
        // Don't show prompt if any media is playing
        if (isMediaPlaying()) {
          return;
        }
        if (onPromptRef.current && !isIdle) {
          onPromptRef.current();
          promptShownRef.current = true;
        }
      }, promptTime);
    }

    timeoutIdRef.current = setTimeout(() => {
      // Don't go idle if any media is playing
      if (isMediaPlaying()) {
        return;
      }
      if (!isIdle) {
        setIsIdle(true);
        if (onIdleRef.current) {
          onIdleRef.current();
        }
        if (stopOnIdle) {
          clearTimers();
        }
        if (crossTab && broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'idle' });
        }
      }
    }, remainingTime);
  }, [
    disabled,
    isPaused,
    isIdle,
    timeout,
    promptBeforeIdle,
    stopOnIdle,
    crossTab,
    clearTimers,
  ]);

  useEffect(() => {
    resetTimersRef.current = resetTimers;
  }, [resetTimers]);

  const handleActivity = useCallback(
    (event: Event) => {
      if (disabled || isPaused) {
        return;
      }

      if (debounceTimeoutIdRef.current) {
        clearTimeout(debounceTimeoutIdRef.current);
      }

      debounceTimeoutIdRef.current = setTimeout(() => {
        const wasIdle = isIdle;
        const wasPromptShowing = promptShownRef.current;
        const now = Date.now();
        lastActivityRef.current = now;
        setIsIdle(false);

        // Call onActive if user was idle and becomes active, but only if prompt is not showing.
        // If prompt is showing, regular activity should not dismiss it - only explicit activate() should.
        if (wasIdle && !wasPromptShowing && onActiveRef.current) {
          onActiveRef.current();
        }

        if (crossTab && broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({ type: 'activity' });
        }

        // Don't reset timers if prompt is showing - let the countdown continue
        // This ensures the countdown shows the correct remaining time
        if (!wasPromptShowing) {
          resetTimers();
        }

        if (onActionRef.current) {
          onActionRef.current(event, {
            getRemainingTime: () => {
              if (timerStartTimeRef.current === null) {
                return timeout;
              }
              const elapsed = Date.now() - timerStartTimeRef.current;
              const remaining = timeout - elapsed;
              return Math.max(0, remaining);
            },
          });
        }
      }, debounce);
    },
    [disabled, isPaused, isIdle, timeout, debounce, crossTab, resetTimers],
  );

  useEffect(() => {
    if (!crossTab || disabled || typeof BroadcastChannel === 'undefined') {
      return;
    }

    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannelRef.current = channel;

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'activity') {
        const now = Date.now();
        lastActivityRef.current = now;
        elapsedTimeRef.current = 0;
        timerStartTimeRef.current = now;
        setIsIdle(false);
        if (resetTimersRef.current) {
          resetTimersRef.current();
        }
        if (onActiveRef.current) {
          onActiveRef.current();
        }
        promptShownRef.current = false;
      } else if (event.data.type === 'idle') {
        setIsIdle(true);
        if (onIdleRef.current) {
          onIdleRef.current();
        }
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [crossTab, disabled]);

  useEffect(() => {
    if (disabled || isPaused) {
      return;
    }

    const events: (keyof WindowEventMap)[] = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const options: AddEventListenerOptions = { passive: true, capture: true };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, options);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleActivity(new Event('visibilitychange'));
        if (onActiveRef.current) {
          onActiveRef.current();
        }
        promptShownRef.current = false;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity, options);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimeoutIdRef.current) {
        clearTimeout(debounceTimeoutIdRef.current);
      }
    };
  }, [disabled, isPaused, handleActivity]);

  useEffect(() => {
    if (!disabled && !isPaused) {
      const now = Date.now();
      lastActivityRef.current = now;
      timerStartTimeRef.current = now;
      elapsedTimeRef.current = 0;
      resetTimers();
    }

    return () => {
      clearTimers();
    };
  }, [disabled, isPaused, resetTimers, clearTimers]);

  const activate = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    timerStartTimeRef.current = now;
    elapsedTimeRef.current = 0;
    setIsIdle(false);
    promptShownRef.current = false;
    resetTimers();

    if (crossTab && broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'activity' });
    }

    if (onActiveRef.current) {
      onActiveRef.current();
    }
  }, [resetTimers, crossTab]);

  const getRemainingTime = useCallback((): number => {
    if (disabled || isPaused || isIdle || timerStartTimeRef.current === null) {
      return 0;
    }
    const elapsed = Date.now() - timerStartTimeRef.current;
    const remaining = timeout - elapsed;
    return Math.max(0, remaining);
  }, [disabled, isPaused, isIdle, timeout]);

  const start = useCallback(() => {
    setIsPaused(false);
    setIsIdle(false);
    const now = Date.now();
    lastActivityRef.current = now;
    timerStartTimeRef.current = now;
    elapsedTimeRef.current = 0;
    resetTimers();
  }, [resetTimers]);

  const pause = useCallback(() => {
    setIsPaused(true);
    if (timerStartTimeRef.current !== null) {
      elapsedTimeRef.current += Date.now() - timerStartTimeRef.current;
    }
    clearTimers();
  }, [clearTimers]);

  const resume = useCallback(() => {
    setIsPaused(false);
    const now = Date.now();
    lastActivityRef.current = now;
    timerStartTimeRef.current = now;
    elapsedTimeRef.current = 0;
    resetTimers();
  }, [resetTimers]);

  const isEnabled = useCallback(() => {
    return !disabled;
  }, [disabled]);

  return {
    activate,
    getRemainingTime,
    start,
    pause,
    resume,
    isEnabled,
  };
}
